<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl font-headline font-medium text-bhav-blue-600">Available Homes</h2>
    </div>

    <div class="flex w-full flex-col flex-wrap md:flex-row md:justify-start gap-4 mt-8">
        <div class="mb-4 flex-initial min-w-80">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" class="min-w-100" color="gray" placeholder="Search available homes by keyword" />
        </div>
        <div v-if="availableCollections.length > 1" class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.collections" label="Collections" :options="availableCollections.map(collection => ({label: collection.meta?.info?.name, value: collection.community_rid}))" empty="All Collections" all="All Collections" />
        </div>
        <div v-if="availableFloorplans.length > 1" class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.floorplans" label="Floorplans" :options="availableFloorplans.map(floorplan => ({label: floorplan.meta?.info?.name, value: floorplan.kova.ModelRID}))" empty="All Flooplans" all="All Floorplans" />
        </div>
        <div class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses"
                all="All Statuses" />
        </div>
        <div class="mb-4 flex-initial">
            <SelectField v-model="query.lot" label="Lot Status" empty="All Statuses" :options="lot_statuses" />
        </div>
        <div class="mb-4 flex-initial">
            <SelectField v-model="query.type" label="Type" :options="[{label: 'Market', value: 'Market'}, {label: 'Model', value: 'Model'}]" empty="All Types" :allow-null="true" />
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
            <FormButton @click="clear" color="blue">Clear Filters</FormButton>
        </div>
        <div class="mb-4 flex-initial sm:mt-6 ml-auto">
            <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
                <p class="relative">
                    <span class="fak fa-col-options leading-none"></span>
                    <span class="sr-only">Open Table Column Preferences Drawer</span>
                </p>
            </button>
        </div>
    </div>

    <PaginationTable empty-message="There are currently no available home items." :data="filtered" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false" :show-columns="showColumns" @closeDrawer="showColumns = false">
        <template #col-address="{ row }">
            <router-link :to="link(row)"
                class="py-4 text-bhav-blue-500 hover:text-bhav-blue-600 block whitespace-nowrap">
                {{ row.kova?.Lot?.StreetAddress }}<br>
                {{ row.kova?.Lot?.City }}, {{ row.kova?.Lot?.State }} {{ row.kova?.Lot?.ZipCode }}
            </router-link>
        </template>

        <template #col-collection="{ row }">
            <div class="py-4">
                {{ getCollection(row.kova.CommunityRID)?.meta?.info?.name }}
            </div>
        </template>

        <template #col-floorplan="{ row }">
            <div class="py-4">
                {{ getFloorplan(row.kova.ModelRID)?.meta?.info?.name }}
            </div>
        </template>
        
        <template #col-price="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="link(row)">
            <div class="text-sm leading-5 text-oakwood-gray-900">
              {{ formatToCurrency(row.meta?.info?.price || row.kova?.Price) }}
            </div>
          </router-link>
        </template>

        <template #col-status="{ row }">
            <div class="py-4">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="[row.kova?.Lot?.Status === 'Available' ? 'bg-bhav-green-100 text-bhav-green-600' : 'bg-bhav-blue-100 text-bhav-blue-600']">
                    {{ row.kova?.Lot?.Status }}
                </span>
            </div>
        </template>

        <template #col-type="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="link(row)">
            <span
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              :class="[row.kova?.Lot?.Type === 'Model' ? 'bg-oakwood-orange-200 text-oakwood-orange-800' : 'bg-bhav-green-100 text-bhav-green-600']">
              {{ row.kova?.Lot?.Type }}
            </span>
          </router-link>
        </template>

        <template #col-lot="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.kova?.Lot.LotNumber ?? '-' }}
          </router-link>
        </template>

        <template #col-elevation="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.kova?.HouseName ?? '-' }}
          </router-link>
        </template>

        <template #col-bedrooms="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.meta?.info?.bedrooms || '-' }}
          </router-link>
        </template>

        <template #col-bathrooms="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ formatToNumber(((row.meta?.info?.bathrooms.full || 0) * 1) + ((row.meta?.info?.bathrooms.half || 0) * .5)) }}
          </router-link>
        </template>

        <template #col-garages="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ row.meta?.info?.garages || '-' }}
          </router-link>
        </template>
        
        <template #col-sqft="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ formatToNumber(row.meta?.info?.sqft) || '-' }}
          </router-link>
        </template>

        <template #col-latlng="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            <dl>
              <dt class="sr-only">Latitude:</dt>
              <dd><span class="far fa-arrows-up-down text-bhav-blue-400 w-4 text-center"></span> {{ formatToNumber(row.meta.info?.geo?.lat || 0) }}°</dd>
              <dt class="sr-only">Longitude:</dt>
              <dd><span class="far fa-arrows-left-right text-bhav-blue-400 w-4 text-center"></span> {{ formatToNumber(row.meta.info?.geo?.lng || 0) }}°</dd>
            </dl>
          </router-link>
        </template>

        <template #col-rate="{ row }">
          <router-link class="block py-4" :to="link(row)">
            {{ row.meta?.info?.rate || '-' }}
          </router-link>
        </template>

        <template #col-callout="{ row }">
          <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-oakwood-green-100 text-oakwood-green-800">
              {{ !['',null,'N/S'].includes(row.meta?.info?.callout) ? row.meta?.info?.callout : '' }}
            </span>
          </router-link>
        </template>

        <template #col-notes="{ row }">
          <router-link class="block py-4" :to="link(row)">
            {{ row.meta?.info?.notes || '-' }}
          </router-link>
        </template>

        <template #col-published="{ row }">
            <div v-if="GlobalPolicy.edit()" class="py-4 flex items-center gap-2 whitespace-nowrap">
                <ToggleButton :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'"
                    :label="row.status == 'active' ? 'Published' : 'Unpublished'" />
                <label :for="`item-${row.id}-status`">{{ Statuses.label(row.status) }}</label>
            </div>
            <div v-else class="py-4">
                {{ Statuses.label(row.status) }}
            </div>
        </template>
    </PaginationTable>

</template>

<script>
import axios from 'axios';
import { FormButton, SelectField, SelectMultipleField, ToggleButton } from '@/components/form';
import PaginationTable from '@/components/PaginationTable.vue';
import Statuses from '@/enums/Statuses';
import { GlobalPolicy } from '@/policies/global';
import { formatToCurrency, formatToNumber } from '@/helpers/numberHelpers';
import { useMessageStore } from '@/stores/message';
import { mapActions } from 'pinia';
import SearchInput from '../form/SearchInput.vue';

const query = {
    search: '',
    collections: [],
    floorplans: [],
    lot: null,
    status: ['new', 'active', 'inactive'],
    type: null
}

export default {
    setup() {
        return {
            GlobalPolicy,
            Statuses,
            formatToCurrency,
            formatToNumber,
            link: (row) => ({ name: 'available-homes-show', params: { id: row.id } }),
            lot_statuses: [
                {
                    label : 'Available',
                    value : 'Available'
                },
                {
                    label : 'New',
                    value : 'New'
                }
            ]
        }
    },
    data() {
        return {
            query: { ...query },
            showColumns: false
        }
    },
    emits: [
        'update'
    ],
    props: {
        sales_orders: {
            type: Array,
            required: true
        },
        floorplans: {
            type: Array,
            required: true
        }
    },
    methods: {
        ...mapActions(useMessageStore, ['setAlert']),
        save(item) {
            const status = item.status;
            item.status = item.status != 'active' ? 'active' : 'inactive';
            axios.patch(`sales_orders/${item.id}`, item)
                .then(res => {
                    if (res.data?.id) {
                        this.$emit('update', res.data);
                    }
                })
                .catch(e => {
                    item.status = status;
                    throw e;
                });
        },
        validate(item) {
            return item.status === 'active' || 
            (
                (item.meta.info?.geo?.lat || false)
                && (item.meta.info?.geo?.lng || false) 
                && (item.meta.info?.bedrooms || 0) > 0
                && (item.meta.info?.bathrooms.half ?? null) !== null
                && (item.meta.info?.bathrooms.full || 0) > 0
                && (item.meta.info?.garages ?? null) !== null
                && (item.meta.info?.price || 0) > 0
                && (item.meta.info?.sqft || 0) > 0
            )
        },
        getFloorplan(model_rid, community_rid) {
            return this.floorplans.find(floorplan => floorplan.kova.ModelRID == model_rid) || false;
        },
        getCollection(id) {
            return this.collections.find(community => community.kova.CommunityRID == id) || false;
        },
        clear() {
            this.query = { ...query };
        }
    },
    computed: {
        filtered() {
            let results = this.sales_orders;
            if (this.query.status.length) {
                results = results.filter(item => this.query.status.includes(item.status));
            }

            if (this.query.lot !== null) results = results.filter(item => item.kova.Lot?.Status === this.query.lot);

            if (this.query.type !== null) {
                results = results.filter(item => this.query.type === item.kova?.Lot?.Type);
            }

            if (this.query.floorplans.length > 0) {
                results = results.filter(item => this.query.floorplans.includes(item.kova?.ModelRID));
            }

            if (this.query.collections.length > 0) {
                results = results.filter(item => this.query.collections.includes(item.kova?.CommunityRID));
            }

            if (this.query.search != '') {
                const searchTerm = this.query.search?.toLowerCase() || '';
                results = results.filter(item => {
                    const address = (`${item.kova?.Lot?.StreetAddress || ''}, ${item.kova?.Lot?.City || ''}, ${item.kova?.Lot?.State || ''} ${item.kova?.Lot?.ZipCode || ''}`).toLowerCase();
                    const price = (formatToCurrency(item.meta?.info?.price || item.kova?.Price || '')).toString().toLowerCase();
                    const lot = (item.kova?.Lot?.LotNumber || '').toString().toLowerCase();
                    const elevation = (item.kova?.HouseName || '').toLowerCase();
                    const bathrooms = (formatToNumber(((item.meta?.info?.bathrooms?.full || 0) * 1) + ((item.meta?.info?.bathrooms?.half || 0) * .5))).toString().toLowerCase();
                    const bedrooms = (item.meta?.info?.bedrooms || '').toString().toLowerCase();
                    const garages = (item.meta?.info?.garages || '').toString().toLowerCase();
                    const sqft = (formatToNumber(item.meta?.info?.sqft || '')).toString().toLowerCase();
                    const lat = (formatToNumber(item.meta?.info?.geo?.lat || 0)).toString().toLowerCase();
                    const lng = (formatToNumber(item.meta?.info?.geo?.lng || 0)).toString().toLowerCase();
                    const rate = (item.meta?.info?.rate || '').toLowerCase();
                    const callout = (item.meta?.info?.callout || '').toLowerCase();
                    const notes = (item.meta?.info?.notes || '').toLowerCase();

                    return address.includes(searchTerm) ||
                           price.includes(searchTerm) ||
                           lot.includes(searchTerm) ||
                           elevation.includes(searchTerm) ||
                           bathrooms.includes(searchTerm) ||
                           bedrooms.includes(searchTerm) ||
                           garages.includes(searchTerm) ||
                           sqft.includes(searchTerm) ||
                           lat.includes(searchTerm) ||
                           lng.includes(searchTerm) ||
                           rate.includes(searchTerm) ||
                           callout.includes(searchTerm) ||
                           notes.includes(searchTerm);
                });
            }

            return results;
        },
        availableFloorplans() {
            const model_rids = [...new Set(this.sales_orders.map(sales_order => sales_order.kova.ModelRID))];
            return this.floorplans.filter(floorplan => model_rids.includes(floorplan.kova.ModelRID)).sort((a, b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
        },
        collections() {
            if (!this.floorplans[0]?.collection) return [];
            return [...new Set(this.floorplans.map(floorplan => JSON.stringify(floorplan.collection)))].map(floorplan => JSON.parse(floorplan))
        },
        availableCollections() {
            const community_rids = [...new Set(this.sales_orders.map(sales_order => sales_order.kova.CommunityRID))];
            return this.collections.filter(collection => community_rids.includes(collection.community_rid)).sort((a, b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);

        },
        hasFilters() {
            return JSON.stringify(query) !== JSON.stringify(this.query);
        },
        columns() { 
            let columns = [
                {
                    name: 'address',
                    label: 'Address',
                    sort: (row) => `${row.kova?.Lot?.StreetAddress}, ${row.kova?.Lot?.City}, ${row.kova?.Lot?.State} ${row.kova?.Lot?.ZipCode}`,
                    default: true
                },
                {
                    name: 'floorplan',
                    label: 'Floorplan',
                    sort: (row) => this.getFloorplan(row.kova.ModelRID)?.meta?.info?.name,
                    default: true
                },
                {
                    name: 'price',
                    label: 'Price',
                    sort: (row) => row.meta?.info?.price || row.kova?.Price || 0,
                    default: false
                },
                {
                    name: 'status',
                    label: 'Lot Status',
                    sort: (row) => row.kova?.Lot?.Status || '',
                    default: true
                },
                {
                    name: 'type',
                    label: 'Type',
                    sort: (row) => row.kova?.Lot?.Type || '',
                    default: false
                },
                {
                    name: 'lot',
                    label: 'Lot',
                    sort: (row) => row.kova?.Lot.LotNumber || 0,
                    default: false
                },
                {
                    name: 'elevation',
                    label: 'Elevation',
                    sort: (row) => row.kova?.HouseName || '',
                    default: false
                },
                {
                    name: 'bedrooms',
                    label: 'Bedrooms',
                    sort: (row) => row.meta?.info?.bedrooms ?? 0,
                    default: false
                },
                {
                    name: 'bathrooms',
                    label: 'Bathrooms',
                    sort: (row) => (row.meta?.info?.bathrooms.full ?? 0) + ((row.meta?.info?.bathrooms.half ?? 0) * .5),
                    default: false
                },
                {
                    name: 'garages',
                    label: 'Garages',
                    sort: (row) => row.meta?.info?.garages ?? 0,
                    default: false
                },
                {
                    name: 'sqft',
                    label: 'Square Footage',
                    sort: (row) => row.meta?.info?.sqft ?? 0,
                    default: false
                },
                {
                    name: 'latlng',
                    label: 'Lat/Lng',
                    sort: (row) => (row.meta.info?.geo?.lat || 0),
                    default: false
                },
                {
                    name: 'rate',
                    label: 'Advertised Rate',
                    sort: (row) => row.meta?.info?.rate ?? '',
                    default: false
                },
                {
                    name: 'callout',
                    label: 'Callout',
                    sort: (row) => row.meta?.info?.callout ?? 'N/S',
                    default: false
                },
                {
                    name: 'notes',
                    label: 'Internal Notes',
                    sort: (row) => row.meta?.info?.notes ?? '',
                    default: false
                },
                {
                    name: 'published',
                    label: 'Published',
                    sort: (row) => Statuses.label(row.status),
                    default: true
                },
            ];

            if(this.collections?.length ?? 0 > 0) {
                columns.splice(
                    2,
                    0,
                    {
                        name: 'collection',
                        label: 'Collection',
                        sort: (row) => this.getCollection(row.kova.CommunityRID)?.meta?.info?.name,
                        default: true
                    }
                )
            }

            return columns;
        }
    },
    components: {
        ToggleButton,
        PaginationTable,
        SelectMultipleField,
        SelectField,
        FormButton,
        SearchInput
    }
}
</script>